export type user = {
  name: string
}

export enum EventType {
  SignedUp = 'SIGNED_UP',
  AssignedToSite = 'ASSIGNED_TO_SITE',
  NewReferral = 'NEW_REFERRAL',
  //FilledQualificationForm = 'FILLED_QUALIFICATION_FORM',
  FailedContactAttempt = 'FAILED_CONTACT_ATTEMPT',
  UploadedImage = 'UPLOADED_IMAGE',
  SubjectEvaluatedSuitable = 'SUBJECT_EVALUATED_SUITABLE', // to be deleted
  SubjectEvaluatedNotSuitable = 'SUBJECT_EVALUATED_NOT_SUITABLE', // to be deleted

  ReadyForEvaluation = 'READY_FOR_EVALUATION',
  //
  // this is not directly added - since we assigne the user to a site
  // meaning we put AssignedToSite on the user and create an event for the user
  //
  SubjectEligible = 'SubjectEligible',
  //
  // this is added after prescreening
  //
  SubjectNotEligeble = 'SubjectNotEligeble',

  SignedConsent = 'SIGNED_CONSENT',
  CancelSignedConsent = 'CANCEL_SIGNED_CONSENT',
  ReviewingInformation = 'REVIEWING_INFORMATION',
  ScheduledForScreening = 'SCHEDULED_FOR_SCREENING',
  CancelPlanedScreening = 'CANCEL_PLANED_SCREENING',
  ScheduledForRandomisation = 'SCHEDULED_FOR_RANDOMISATION',
  CancelPlanedRandomisation = 'CANCEL_PLANED_RANDOMISATION',
  SubjectRandomised = 'SUBJECT_RANDOMISED',
  CancelRandomisation = 'CANCEL_RANDOMISATION',
  Reengaged = 'REENGAGED',
  //SubjectRereferred = 'SUBJECT_REREFERRED',
  OtherEvent = 'OTHER_EVENT',
  //SubjectReferredToEvaluation = 'SUBJECT_REFERRED_TO_EVALUATION',
  NotReachedByEmail = 'NOT_REACHED_BY_EMAIL',
  NotReachedByPhone = 'NOT_REACHED_BY_PHONE',
  NotReached = 'NOT_REACHED',
  WaitingForReply = 'WAITING_FOR_REPLY',
  NoShow = 'NO_SHOW',
  Rescheduled = 'RESCHEDULED',
  FollowUp = 'FOLLOW_UP',
  TravelDistanceTooLong = 'TRAVEL_DISTANCE_TOO_LONG',
  OtherCommitments = 'OTHER_COMMITMENTS',
  RestrictiveWorkSchedule = 'RESTRICTIVE_WORK_SCHEDULE',
  NoLongerWantsToParticipate = 'NO_LONGER_WANTS_TO_PARTICIPATE',
  PrefersCurrentTreatment = 'PREFERS_CURRENT_TREATMENT',
  SubjectWithdrewConsent = 'SUBJECT_WITHDREW_CONSENT',
  TooManyFailedContactAttempts = 'TOO_MANY_FAILED_CONTACT_ATTEMPTS',
  OtherMotivationalDisqualification = 'OTHER_MOTIVATIONAL_DISQUALIFICATION',
  ConditionWasMisdiagnosed = 'CONDITION_WAS_MISDIAGNOSED',
  ConditionInsufficientlySevere = 'CONDITION_INSUFFICIENTLY_SEVERE',
  ConditionTooSevere = 'CONDITION_TOO_SEVERE',
  SymptomAreaOutOfRequiredRange = 'SYMPTOM_AREA_OUT_OF_REQUIRED_RANGE',
  SubjectAgeOutOfRequiredRange = 'SUBJECT_AGE_OUT_OF_REQUIRED_RANGE',
  OtherMedicalDisqualification = 'OTHER_MEDICAL_DISQUALIFICATION',

  //new phonescreen status:
  ReadyForPhoneScreen = 'READY_FOR_PHONESCREEN',

  /** new wrapper project status: */
  BecameDbResponder = 'BECAME_DB_RESPONDER',

  /** the dbResponder finished all prescreening. Waiting to be contacted regarding a future study that they might be eligible for. */
  WaitingDbResponder = 'WAITING_DB_RESPONDER',
}

/** In logical order. E.g. if a responder is scheduled for rando we know that the person also have been scheduled for screening. */
export const enrollmentFunnelInOrder = [
  EventType.SignedUp,
  //EventType.BecameDbResponder, // optional.
  EventType.AssignedToSite,
  EventType.ReadyForEvaluation,
  EventType.ReadyForPhoneScreen,
  EventType.NewReferral,
  EventType.ScheduledForScreening,
  EventType.ScheduledForRandomisation,
  EventType.SubjectRandomised,
]

export type Project = {
  _id: string
  active: boolean
  projectRef: string
  title: string
  condition: string
  age: [low: number, high: number]
  sites: string[]
  questionnaires: string[]
  languages: LanguageISOCode[]
  analytics: Analytics
}

export type ProjectWithSites = Omit<Project, 'sites'> & { sites: Site[] }

export type Analytics = {
  goals: ContractGoal[]
  /** Facebook ad account */
  adAccount?: 'Studies&Me' | 'Sanos'
  /** Facebook campaign ids */
  campaigns?: string[]
  /** dkk */
  adBudget?: number
  /** 0 - 1
   * When starting a project, this is calculated - typically by sponsor.
   */
  expectedScreenFailRate?: number
  /** Sites should be manually linked to specific Facebook Campaigns */
  sitesConfig: { [siteId: string]: SiteCampaigns }
}

export type SiteCampaigns = { budget?: number; sanosAdAccountCampaignIds: string[]; snmeAdAccountCampaignIds: string[] }

export type Site = {
  _id: string
  //_id: Schema.Types.ObjectId
  projectId: string
  internalName: string
  officialName: string
  position: { lng: number; lat: number }
  loc: { type: string; coordinates: number[] }
  state: string
  country: string
  address: string
  /** allow users to see the exact address/clinic name. */
  publicAddress: boolean
  zipcode?: string
  city: string
  capacity: number
  active: boolean
  greenlight: string
  ccStudyId?: number
  ccSiteId?: number
  /** If we have a ccStudyId: Sync state updates from cc? See Automation repo. */
  ccSyncEnabled?: boolean
  /** Sanos sites can move enrollments to other sites (the dashboard function). */
  isSanosSite?: boolean
  reach: number
  lastMoveAt?: string // a timestamp last time the site moved a subject
  lastStatusAt?: string // a timestamp last time the site add a status
  /** if false, Responders skip the phone-screening step, and ends in "tasks" in Sitetool. */
  hasPhoneScreening?: boolean
  customId?: string
}

export type ContractGoal = {
  totalRandomizations: number
  effectiveDate: string // isostring
  deadline: string //isostring
  goalType?: 'Exact' | 'Or more' | 'Or less'
}

enum ReachType {
  /** All good. Close to site. */
  WITHIN_REACH = 'WITHIN_REACH',
  /** Outside primary range, but willing to travel extra km's. */
  WITHIN_TRAVEL = 'WITHIN_TRAVEL',
  OUTSIDE = 'OUTSIDE',
  NOT_DEFINED = 'NOT_DEFINED',
}

// See https://www.loc.gov/standards/iso639-2/php/English_list.php for specification, add as needed
// Not sure if something built in is smarter for this though...
export type LanguageISOCode = 'en' | 'es' | 'da'

export type AuditLogChangeReason =
  | {
      type: 'ENTRY_ERROR'
    }
  | {
      type: 'DATA_UPDATED'
    }
  | {
      type: 'OTHER'
      comment: string
    }

export type EnrollmentType = {
  subjectId: string
  projectId: string
  evaluation?: string
  /** Set after eligible prescreening. */
  siteId?: string
  /** ONLY USED FOR AUDIT TRAIL.
   * All subject logs needs to be shown for a site - even those are not eligibile.
   * So this is to show the logs for non eligible subjects for a site.
   *
   * This is just the nearest site for all non-eligible subjects.
   * But has to be within project.maxDistanceToTravel.
   */
  siteIdNonEligible?: string
  questionnaireId?: string
  project: string // can might be remove - double check with questionnnaire
  zipcode: string
  /** [long, lat] (mapbox format) */
  zipcodeCenter: [number, number]
  city: string
  displayName: string
  locationId: string
  language: string
  position: {
    ip: string
    countryCode: string
    loc: {
      //type: string
      coordinates: number[]
    }
  }

  withinReach: boolean // inde
  willTravel?: boolean | null
  reach: ReachType

  // sitetool specific
  reachedAt?: string
  stateType: EventType

  //followUpBy?: Date // indicates when the user should be blinking
  appointmentDate?: string

  /** seems to be for choosing retention e-mails */
  lastActive?: string
  startedPrescreeningAt?: string
  finishedPrescreeningAt?: string

  acceptTerms: boolean
  acceptCommercial?: boolean
  acceptConsent: string

  utm: {
    utm_campaign?: string[]
    utm_content?: string[]
    utm_medium?: string[]
    utm_source?: string[]
    utm_study?: string[]
    visits?: number
    /** Facebook browserID at signup */
    fbp?: string
    /** Facebook ad-clickID at signup */
    fbc?: string
    /** The score that is sent to facebook.
     * 0-299 for failed, 1000 for passed all inclusioncriteria. */
    lead_score?: number
    /** IP for facebook event matching */
    ip?: string
    /** User Agent for facebook event matching */
    user_agent?: string
    /** The browser URL for the signup. Used for facebook events (required - event_source_url). Should match the verified pixel domain. */
    signup_url?: string
  }

  subscribed: boolean

  /** Just for analytics */
  startedPhoneScreeningAt?: string

  /** ccPatient = SnMe Subject (the actual person)
   * (If the enrollment is registered in Clinical Conductor) */
  ccPatientId?: number
  /** ccSubject = SnMe enrollment (the participation in a study) So this is the important part to sync. */
  ccSubjectId?: number

  //consentNews: boolean
  //consentStudy: boolean

  // creted automaticcluy
  /*updatedAt: Date
	createdAt: Date*/

  /** Responders can signup for the general responder database.
   * So each signup can have 2 enrollment: 1 for the original project and 1 for the responder database copy.
   * We use 2 copies for regulatory reasons, so the original project pi can the failed enrollment.
   *
   * true = "yes"
   * false = "no"
   * undefined = "not asked"
   */
  isDbResponder?: boolean
  /** It the dbresponder is a copy, then we keep the original enrollment. */
  originalEnrollment?: string
  /** For wrapperprojects: Which childProject is currently being evaluated for?
   *
   * This value is dynamically changing when a Responder answers the questionnaire
   * in order to determine which project the Responder is eligible for.
   *
   * This is only set if isDbResponder = true.
   */
  evalProjectId?: string
  /** We have site-specific questions. But we only set siteId after successful prescreening.
   * Therefore we need a temporary site to evaluate for.
   */
  evalSiteId?: string

  /** Only used by the stats page, to calculate how many people stopped at each question. */
  currentQuestionId?: string | 'askBecomeDbResponder'

  /** Signed up on signup.sanos.com. So the branding should be masked to Sanos instead of SnMe. */
  isSanosResponder?: boolean

  /** Each landing page can set a custom facebook pixel id.
   * Used to optimize for different regions.
   */
  customFacebookPixel?: string

  /** We import some subject from CC. (database) */
  isCCResponder?: boolean
}

export type EnrollmentWithId = EnrollmentType & {
  _id: string
  updatedAt: Date
  createdAt: Date
  followUpBy?: Date
}

export enum AssignmentFilterEnum {
  All = 'ALL',
  OnlyAdminAssigned = 'ONLY_ADMIN_ASSIGNED',
  OnlySiteUserAssigned = 'ONLY_SITE_USER_ASSIGNED',
  ExcludingAssigned = 'EXCLUDING_ASSIGNED',
}

export enum AgeGroupEnum {
  AGE_1_TO_10 = '1-10',
  AGE_10_TO_20 = '10-20',
  AGE_20_TO_30 = '20-30',
  AGE_30_TO_40 = '30-40',
  AGE_40_TO_50 = '40-50',
  AGE_50_TO_60 = '50-60',
  AGE_60_TO_70 = '60-70',
  AGE_70_TO_80 = '70-80',
  AGE_80_TO_90 = '80-90',
  AGE_90_TO_100 = '90-100',
  AGE_100_PLUS = '100+',
}
